import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
  TableSortLabel,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  FormGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { Upload as UploadIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

// Munkakör opciók - a dolgozói modell már nem csak sofőröket tartalmaz
const POSITION_OPTIONS = ['sofőr', 'diszpécser', 'szerelő', 'elszámoltató', 'egyéb'];

const DriverList = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');
  const { getAuthHeaders, hasPermission } = useAuth();
  
  // Jogosultság ellenőrzés - szerkesztéshez kell a canManageDrivers jogosultság
  const canEdit = hasPermission('canManageDrivers');
  
  // Szerkesztő dialógus állapotok
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    employeeId: '', 
    pinCode: '',
    email: '',
    employmentStartDate: '',
    employmentEndDate: '',
    status: 'active',
    faultSearchEnabled: false,
    vehicleFaultsEnabled: false,
    ticketEligible: false,
    position: 'sofőr',
    positionOther: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/Drivers', {
        headers: getAuthHeaders()
      });
      setDrivers(response.data);
      setError(null);
    } catch (err) {
      setError('Nem sikerült betölteni a sofőröket. Kérjük, próbálja újra később.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const lower = file.name.toLowerCase();
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv'))
    {
      setSelectedFile(file);
    } else {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Csak .xlsx, .xls vagy .csv fájl tölthető fel.'
      });
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    console.log('Fájl feltöltése:', selectedFile.name, selectedFile.size, 'byte');

    try {
      setUploading(true);
      console.log('API hívás kezdése: /api/Drivers/import');
      const response = await axios.post('/api/Drivers/import', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000
      });
      console.log('API válasz:', response.data);

      setSnackbar({
        open: true,
        severity: 'success',
        message: response.data?.message || 'Importálás sikeres.'
      });
      setImportDialogOpen(false);
      setSelectedFile(null);
      await loadDrivers();
    } catch (err) {
      console.error('Import hiba:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || 'Hiba az importálás során.'
      });
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU');
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getStatusLabel = (status) => {
    return status === 'active' ? 'Aktív' : 'Inaktív';
  };

  // Szerkesztő dialógus kezelés
  const handleOpenEditDialog = (driver = null) => {
    if (driver) {
      setEditingDriver(driver);
      const knownPosition = driver.position && POSITION_OPTIONS.includes(driver.position) ? driver.position : (driver.position ? 'egyéb' : 'sofőr');
      setFormData({
        name: driver.name || '',
        employeeId: driver.employeeId || '',
        pinCode: driver.pinCode || '',
        email: driver.email || '',
        employmentStartDate: formatDateForInput(driver.employmentStartDate),
        employmentEndDate: formatDateForInput(driver.employmentEndDate),
        status: driver.status || 'active',
        faultSearchEnabled: driver.faultSearchEnabled || false,
        vehicleFaultsEnabled: driver.vehicleFaultsEnabled || false,
        ticketEligible: driver.ticketEligible || false,
        position: knownPosition,
        positionOther: knownPosition === 'egyéb' ? (driver.position || '') : ''
      });
    } else {
      setEditingDriver(null);
      setFormData({
        name: '',
        employeeId: '',
        pinCode: '',
        email: '',
        employmentStartDate: '',
        employmentEndDate: '',
        status: 'active',
        faultSearchEnabled: false,
        vehicleFaultsEnabled: false,
        ticketEligible: false,
        position: 'sofőr',
        positionOther: ''
      });
    }
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingDriver(null);
    setFormData({
      name: '',
      employeeId: '',
      pinCode: '',
      email: '',
      employmentStartDate: '',
      employmentEndDate: '',
      status: 'active',
      faultSearchEnabled: false,
      vehicleFaultsEnabled: false,
      ticketEligible: false,
      position: 'sofőr',
      positionOther: ''
    });
  };

  const handleSaveDriver = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, severity: 'error', message: 'A sofőr neve kötelező' });
      return;
    }

    try {
      const resolvedPosition = formData.position === 'egyéb'
        ? (formData.positionOther.trim() || 'egyéb')
        : formData.position;

      const payload = {
        name: formData.name,
        employeeId: formData.employeeId || null,
        pinCode: formData.pinCode || null,
        email: formData.email || null,
        employmentStartDate: formData.employmentStartDate || null,
        employmentEndDate: formData.employmentEndDate || null,
        status: formData.status,
        faultSearchEnabled: formData.faultSearchEnabled,
        vehicleFaultsEnabled: formData.vehicleFaultsEnabled,
        ticketEligible: formData.ticketEligible,
        position: resolvedPosition
      };

      if (editingDriver) {
        await axios.put(`/api/Drivers/${editingDriver.id}`, payload, {
          headers: getAuthHeaders()
        });
        setSnackbar({ open: true, severity: 'success', message: 'Sofőr sikeresen frissítve' });
      } else {
        await axios.post('/api/Drivers', payload, {
          headers: getAuthHeaders()
        });
        setSnackbar({ open: true, severity: 'success', message: 'Sofőr sikeresen létrehozva' });
      }
      handleCloseEditDialog();
      loadDrivers();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Hiba a sofőr mentése során: ' + (err.response?.data?.message || err.message)
      });
    }
  };

  const handleDeleteDriver = async (id) => {
    if (!window.confirm('Biztosan törölni (deaktiválni) szeretnéd ezt a sofőrt?')) {
      return;
    }

    try {
      await axios.delete(`/api/Drivers/${id}`, {
        headers: getAuthHeaders()
      });
      setSnackbar({ open: true, severity: 'success', message: 'Sofőr sikeresen deaktiválva' });
      loadDrivers();
    } catch (err) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Hiba a sofőr törlése során: ' + (err.response?.data?.message || err.message)
      });
    }
  };

  // Szűrt sofőrök
  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.employeeId && driver.employeeId.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = showInactive || driver.status === 'active';
    return matchesSearch && matchesStatus;
  });

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedDrivers = [...filteredDrivers].sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Sofőrök
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEditDialog()}
            >
              Új sofőr
            </Button>
          )}
          {canEdit && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<UploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              Importálás
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDrivers}
          >
            Frissítés
          </Button>
        </Box>
      </Box>

      {/* Szűrő panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Keresés"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Button
            variant={showInactive ? 'contained' : 'outlined'}
            color={showInactive ? 'warning' : 'inherit'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Inaktívak mutatása' : 'Csak aktívak'}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {sortedDrivers.length} sofőr
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Név
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'employeeId'}
                  direction={orderBy === 'employeeId' ? order : 'asc'}
                  onClick={() => handleRequestSort('employeeId')}
                >
                  Azonosító
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'position'}
                  direction={orderBy === 'position' ? order : 'asc'}
                  onClick={() => handleRequestSort('position')}
                >
                  Munkakör
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'employmentStartDate'}
                  direction={orderBy === 'employmentStartDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('employmentStartDate')}
                >
                  Jogviszony kezdete
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'employmentEndDate'}
                  direction={orderBy === 'employmentEndDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('employmentEndDate')}
                >
                  Jogviszony vége
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Státusz
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Létrehozva
                </TableSortLabel>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDrivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="text.secondary">
                    Nincs megjeleníthető sofőr
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedDrivers.map((driver) => (
                <TableRow key={driver.id} hover sx={{ opacity: driver.status === 'active' ? 1 : 0.5 }}>
                  <TableCell>{driver.id}</TableCell>
                  <TableCell>{driver.name}</TableCell>
                  <TableCell>{driver.employeeId || '-'}</TableCell>
                  <TableCell>{driver.position || 'sofőr'}</TableCell>
                  <TableCell>{formatDate(driver.employmentStartDate)}</TableCell>
                  <TableCell>{formatDate(driver.employmentEndDate)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(driver.status)} 
                      color={driver.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(driver.createdAt)}</TableCell>
                  <TableCell>{driver.email || '-'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {canEdit && (
                      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 0 }}>
                        <Tooltip title="Szerkesztés">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenEditDialog(driver)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Törlés">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteDriver(driver.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => !uploading && setImportDialogOpen(false)}>
        <DialogTitle>Sofőrök importálása</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Válassz ki egy Excel vagy CSV fájlt (.xlsx, .xls, .csv) a sofőrök importálásához.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            A fájl első oszlopában (vagy a "rendszámplate" fejlécű oszlopban) legyenek a sofőrök nevei.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
            disabled={uploading}
          >
            Fájl kiválasztása
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </Button>
          {selectedFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Kiválasztott fájl: {selectedFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)} disabled={uploading}>
            Mégse
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Importálás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Szerkesztő Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDriver ? 'Dolgozó szerkesztése' : 'Új dolgozó'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Dolgozó neve"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Azonosító (opcionális)"
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="position-select-label">Munkakör</InputLabel>
              <Select
                labelId="position-select-label"
                label="Munkakör"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              >
                {POSITION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.position === 'egyéb' && (
              <TextField
                label="Munkakör megnevezése"
                value={formData.positionOther}
                onChange={(e) => setFormData({ ...formData, positionOther: e.target.value })}
                fullWidth
                placeholder="pl. karbantartó"
              />
            )}
            <TextField
              label="PIN kód (4-6 számjegy)"
              value={formData.pinCode}
              onChange={(e) => setFormData({ ...formData, pinCode: e.target.value })}
              fullWidth
              placeholder="pl. 1234"
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              label="Email cím"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              placeholder="pl. kiss.janos@tbusz.hu"
            />
            <TextField
              label="Jogviszony kezdete"
              type="date"
              value={formData.employmentStartDate}
              onChange={(e) => setFormData({ ...formData, employmentStartDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Jogviszony vége"
              type="date"
              value={formData.employmentEndDate}
              onChange={(e) => setFormData({ ...formData, employmentEndDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status === 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'active' : 'inactive' })}
                    color="primary"
                  />
                }
                label={formData.status === 'active' ? "Aktív" : "Inaktív"}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.faultSearchEnabled}
                    onChange={(e) => setFormData({ ...formData, faultSearchEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Hibakereső jogosultság"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.vehicleFaultsEnabled}
                    onChange={(e) => setFormData({ ...formData, vehicleFaultsEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Vezényelt jármű hibái jogosultság"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ticketEligible}
                    onChange={(e) => setFormData({ ...formData, ticketEligible: e.target.checked })}
                    color="primary"
                  />
                }
                label="Jogosult menetjegy átvételére/kiadására"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Mégse</Button>
          <Button onClick={handleSaveDriver} variant="contained" color="primary">
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DriverList;
